import Vue from 'vue'
import VueMeta from 'vue-meta'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import router from './router'
import i18n from './i18n'
import store from './store'
import { getBrandInfo, getBanner, getQuickBar, getCarousel } from '@/api/brand'
import { info } from '@/api/user'
import { toErrorPage, loadDynamicCss, loadRefreshCss, setCurrencySymbol, getThemeImagePath } from '@/utils/tool'
import '@/utils/filters'
import { getCurrentLangPackVer } from '@/utils/config'
import Cookies from 'js-cookie'
import CustomSocket from '@/services/socket'
import { forceApp, showNotice, isPublicRoute, showBrandRetryNotice } from '@/utils/tool'
import App from './App.vue'
import { checkThemeRestrictions } from '@/router/themeRestrictions'
import { updateCanonical } from '@/utils/canonical'

if (process.env.VUE_APP_ENV === 'production' && location.protocol === 'http:') {
  location.href = location.href.replace(/^http:/, 'https:')
}

Vue.config.productionTip = false

Vue.use(VueMeta, {
  tagIDKeyName: 'vmid',
  refreshOnceOnNavigation: true
})

// 四舍五入到小数点后两位
Vue.filter('numberFormat', function(price) {
  if (!isNaN(price) && price !== null) {
    return parseFloat(Math.floor(price * 100) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  return price
})

// 完整顯示金額：小數點以下最多 10 位、去尾零、加千分位
Vue.filter('numberFormatFull', function(value) {
  if (value === null || value === undefined || isNaN(value)) return value
  return parseFloat(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 10
  })
})

// 取到小数点后4位並無條件捨去；小數位皆為 0 時不顯示小數部分（600.0000 → 600）
Vue.filter('numberFormat4', function(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0'
  }

  const truncated = Math.floor(Number(value) * 10000) / 10000
  return Number.isInteger(truncated) ? truncated.toString() : truncated.toFixed(4)
})

// 保留三位小数、小數位數皆為 0 時 只顯示 '0'
Vue.filter('numberFormatThird', function(price) {
  if (!isNaN(price) && price !== null) {
    const formatted = parseFloat(price).toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return parseFloat(formatted) === 0 ? '0' : formatted.replace(/(\.0{1,})$/, '')
  }
  return price
})

Vue.prototype.$getThemeImagePath = getThemeImagePath

// 開啟socket
Vue.use(CustomSocket)

Vue.use(Toast)

Vue.config.errorHandler = (err, vm, info) => {
  if (err.message && err.message.includes('Redirected when going from')) {
    return
  }
  console.error('Vue Error:', err, info)
}

window.addEventListener('unhandledrejection', event => {
  if (event.reason && event.reason.message &&
      event.reason.message.includes('Redirected when going from')) {
    event.preventDefault()
    return
  }
})

router.beforeEach((to, from, next) => {
  // 檢查主題限制
  const themeCheck = checkThemeRestrictions(to)
  if (themeCheck.blocked) {
    return next(themeCheck.redirectTo)
  }

  // 線下站不開放註冊：註冊頁導回首頁並開啟登入（首次載入時品牌資料尚未取得，另於取得品牌資料後再檢查一次）
  if (store.getters.isLoginOnly && to.path === '/user/register') {
    if (!store.getters.token) {
      // 延後開啟，避免 Main 初始化時把彈窗關掉
      setTimeout(() => {
        store.commit('SET_LOGIN_OPEN', true)
      }, 200)
    }
    return next('/')
  }

  // 檢查路由重複
  if (to.path.toLowerCase() === from.path.toLowerCase() && to.path !== from.path) {
    return next(false) // Prevent navigation to avoid duplication error
  }

  if (to.meta && to.meta.needRedirect && localStorage.getItem('redirectAfterLogin')) {
    if (!store.getters.token) {
      setTimeout(() => {
        store.commit('SET_LOGIN_OPEN', true)
      }, 200)
    }
  }

  if (process.env.VUE_APP_THEME === 'theme_gold' && to.meta && to.meta.metaInfo) {
    store.commit('CHANGE_META_INFO', to.meta.metaInfo)
  }

  const shareCode = to.query.share_code
  if (shareCode) {
    store.commit('SET_SHARE_CODE', shareCode)
    localStorage.setItem('shareCode', shareCode)
  }

  // 需要登入的頁面判斷
  if (to.meta && to.meta.needLogin) {
    if (!store.getters.token) {
      setTimeout(() => {
        store.commit('SET_FORGET_OPEN', false)
        store.commit('SET_LOGIN_OPEN', true)
      }, 0.1)
      return // Make sure to return here to prevent further navigation
    }
  }

  store.commit('SET_HIDE_MOBILE_NAV', false)

  if (to.meta && to.meta.needCookie) {
    store.commit('SET_COOKIE', true)
  } else {
    store.commit('SET_COOKIE', false)
  }

  if (to.query.btag) {
    store.commit('SET_BTAG_CODE', to.query.btag)
    localStorage.setItem('btag', to.query.btag)
  }

  if (store.getters.token && to.meta && to.meta.isClose) {
    showNotice(i18n.t('Bao.coming_soon'))
    next(false)
  } else if (store.getters.token && store._vm.memberInfo.member_type === 3 && to.meta && to.meta.isLitePlayer) {
    showNotice(i18n.t('Bao.lite_player_notice'))
    next(false)
  } else {
    next()
  }
})

// 切版改版：載入 matched 路由 meta.refreshCss 指定的樣式
// 已載入過的 CSS 直接同步放行，避免導航變成非同步（首頁 watcher 會讀到舊的路由參數）
const loadedRefreshCss = new Set()
router.beforeResolve((to, from, next) => {
  const files = [...new Set(to.matched.flatMap(record => record.meta.refreshCss || []))]
    .filter(file => !loadedRefreshCss.has(file))
  if (!files.length) return next()
  loadRefreshCss(files).then(() => {
    files.forEach(file => loadedRefreshCss.add(file))
    next()
  })
})

// 切版改版：依 matched 路由 meta.bodyClass 切換 body class，改版樣式以此限定範圍
let refreshBodyClasses = []
router.afterEach(to => {
  document.body.classList.remove(...refreshBodyClasses)
  refreshBodyClasses = [...new Set(to.matched.flatMap(record => record.meta.bodyClass || []))]
  document.body.classList.add(...refreshBodyClasses)
})

// brand API 失敗時只開放公開頁：站內前往依賴品牌資料的頁面（錢包、會員中心…）一律擋下並提示重新整理
router.beforeEach((to, from, next) => {
  if (store.getters.brandFailed && !isPublicRoute(to)) {
    showBrandRetryNotice()
    next(false)
  } else {
    next()
  }
})

// canonical 依路由輸出各頁自身網址（取代打包時寫死的首頁 canonical）
router.afterEach(to => {
  updateCanonical(to.path)
})

Vue.directive('visible', function(el, binding) {
  el.style.visibility = binding.value ? 'visible' : 'hidden'
})

store.commit('SET_LOADING_COUNT_ZERO')
store.commit('SET_LOADING', true)
store.commit('SET_LOAD_COMPLETED', false)

store.commit('SET_HIDE_MOBILE_NAV', false)

let brandCode = process.env.VUE_APP_BRAND_CODE // 開發環境指定brand code
if (brandCode === '') {
  brandCode = window.location.host || document.location.host // 剛開始載入先用host取資訊
}

// 重新取相關資訊
store.commit('SET_BRAND_INFO', null)
store.commit('SET_NOTICE_MESSAGE', null)
store.commit('SET_AD_MESSAGE', null)

// 偵測與系參數
let language = new URL(window.location.href || document.location.href).searchParams.get('language') || Cookies.get('language')
let brandObject = {}
if (language !== null && language !== undefined) {
  brandObject = { code: brandCode, language: language }
} else {
  brandObject = { code: brandCode }
}

// 偵測網址token
const queryToken = new URL(window.location.href || document.location.href).searchParams.get('token')
if (queryToken) {
  store.commit('SET_TOKEN', queryToken)
}

// brand API 失敗、拿不到品牌語言清單時的兜底語系（日本站主語系）
const DEFAULT_LANGUAGE = 'jp'
// theme_gold：語言包請求持續 pending 時逾時視同失敗，走兜底語系或解除載入，不可一直停在白頁
const LANG_PACK_TIMEOUT = 15000

// 取語言包並套用：語言包為同網域靜態檔（/locales/*.json），不依賴 brand API
function loadLanguagePack(language, fallbackLanguage) {
  const done = () => {
    store.commit('SET_LOADING', false)
    store.commit('SET_LOAD_COMPLETED', true)
  }

  store.commit('SET_LANGUAGE', language)
  // 確定翻譯檔版本 & 取翻譯內容
  const prevLangPackVer = localStorage.getItem('langPackVer')
  const currentLangPackVer = getCurrentLangPackVer()
  const localLangMessage = localStorage.getItem(`langMsg-${language}`) // 抓cookie值，第一次載入時抓使用者偏好
  let langFilePath = ''

  if (process.env.VUE_APP_ENV === 'development') {
    langFilePath = `/locales/${language}.json`
  } else {
    langFilePath = `/locales/${language}.${currentLangPackVer}.json`
  }

  const getRemoteLangMessage = () => {
    let controller = null
    let timer = null
    if (process.env.VUE_APP_THEME === 'theme_gold' && typeof AbortController !== 'undefined') {
      controller = new AbortController()
      timer = setTimeout(() => controller.abort(), LANG_PACK_TIMEOUT)
    }
    fetch(langFilePath, controller ? { signal: controller.signal } : undefined)
      .then(response => {
        if (!response.ok) {
          throw new Error('HTTP error ' + response.status)
        }
        return response.json()
      })
      .then(json => {
        clearTimeout(timer)
        let Bao
        const theme = process.env.VUE_APP_THEME
        const themeKey = theme || null

        if (themeKey && json[themeKey] && typeof json[themeKey] === 'object') {
          const themeOverrides = json[themeKey]
          const base = { ...json }
          delete base[themeKey]
          // theme_override_keys：記錄哪些 key 來自主題覆寫（例如 footer 判斷 UB 是否有自己的牌照文案）
          Bao = { ...base, ...themeOverrides, theme_override_keys: Object.keys(themeOverrides) }
        } else {
          Bao = json
        }

        const messages = { ...i18n.messages[language], Bao }
        i18n.setLocaleMessage(language, messages)

        done()

        localStorage.setItem('langPackVer', currentLangPackVer)
        localStorage.setItem(`langMsg-${language}`, JSON.stringify(messages)) // 第一次載入時設為使用者瀏覽器偏好語言
      })
      .catch(function(err) {
        clearTimeout(timer)
        console.log(err)
        // 指定語系的語言包不存在時改用兜底語系，仍失敗也要解除載入，不可停在白頁
        if (fallbackLanguage && fallbackLanguage !== language) {
          loadLanguagePack(fallbackLanguage)
        } else {
          done()
        }
      })
  }

  // 如果prevLangPackVer沒值或是版本較舊, 則從server取翻譯, 並更新localstorage
  if (prevLangPackVer && localLangMessage && prevLangPackVer >= currentLangPackVer) {
    try {
      i18n.setLocaleMessage(language, JSON.parse(localLangMessage))// 直接從 localstorage 取
      done()
    } catch (err) {
      console.log(err)
      getRemoteLangMessage()
    }
  } else {
  // 從遠端取並更新 localstorage
    getRemoteLangMessage()
  }
}

async function initApp() {
  if (store.state.user.isSetUsernameActive) {
    store.commit('SET_SET_USERNAME_TOKEN', null)
    store.commit('SET_SET_USERNAME_CREDENTIAL', null)
  }
  store.commit('SET_SET_USERNAME_OPEN', false)

  // 先完成 CSS 載入
  await loadDynamicCss()

  // 標記 CSS 已載入
  window.__baseCssLoaded = true

  // 再創建 Vue 實例
  new Vue({
    router,
    i18n,
    store,
    created() {
      // API 邏輯，但不包含 loadDynamicCss()
      getBrandInfo(brandObject).then((response) => {
      // 自訂js code
        if (response.data.data.brandInfo.custom_script !== '') {
          const script = document.createElement('script')
          script.setAttribute('type', 'text/javascript')
          script.setAttribute('language', 'javascript')
          const inlineScript = document.createTextNode(response.data.data.brandInfo.custom_script)
          script.appendChild(inlineScript)
          document.body.insertAdjacentElement('afterend', script)
        }

        if (forceApp(this.$router.currentRoute.name, response.data.data.permissionInfo.mobile_force_app)) {
          setTimeout(function() { window.location = response.data.data.urlInfo.appDownloadUrl }, 5000)
          showNotice(this.$t('Bao.downAppNotice'), function() {
            window.location = response.data.data.urlInfo.appDownloadUrl
          }, false)
        }

        store.commit('SET_BRAND_INFO', response.data.data)
        store.commit('SET_BRAND_STATUS', 'ready')

        // 線下站不開放註冊：直接進入註冊頁時導回首頁並開啟登入
        if (store.getters.isLoginOnly && this.$router.currentRoute.path === '/user/register') {
          this.$router.replace('/').catch(() => {})
          if (!store.getters.token) {
            // 延後開啟，避免 Main 初始化時把彈窗關掉
            setTimeout(() => {
              store.commit('SET_LOGIN_OPEN', true)
            }, 200)
          }
        }

        setCurrencySymbol()

        document.title = response.data.data.brandInfo.site_title

        // 電話代碼
        const phoneCode = response.data.data.supportPhoneCode
        store.commit('SET_SUPPORT_PHONE_CODE', phoneCode)

        // 國籍
        store.commit('SET_SUPPORT_NATIONALITY', response.data.data.support_nationality)

        // 圖片
        const brandImage = response.data.data.brandImage
        store.commit('SET_BRAND_IMAGE', brandImage)

        // 首頁輪播
        getCarousel().then(response => {
          store.commit('SET_CAROUSEL', response.data.data)
        }).catch(error => {
          console.warn('Failed to fetch carousel (/brand/carousel):', error)
        })

        // 客服
        getQuickBar().then(response => {
          store.commit('SET_QUICKBARS', response.data.data)
        }).catch(error => {
          console.warn('Failed to fetch quick bar (/brand/quick-bar):', error)
        })

        // 語系
        const browserLang = (navigator.language || navigator.userLanguage).toLowerCase()
        let lang
        switch (browserLang) {
          case 'ja':
          case 'ja-jp':
            lang = 'jp'
            break
          case 'en':
          case 'en-us':
          case 'en-gb':
            lang = 'en'
            break
          case 'zh':
          case 'zh-tw':
            lang = 'tw'
            break
          case 'pt':
          case 'pt-br':
            lang = 'pt-BR'
            break
          default:
            lang = 'en' // Default language if no match is found
        }
        const languages = response.data.data.brandInfo.language

        if (!languages || languages.length === 0) {
          toErrorPage('Cant get language')
        } else {
          store.commit('SET_SUPPORT_LANGUAGES', languages)
          if (!language) {
            if (languages.includes(lang)) {
              language = lang
            } else { language = languages[0] }
          }

          loadLanguagePack(language)
        }

        getBanner().then(response => {
          store.commit('SET_BANNERS', response.data.data)
        }).catch(error => {
          console.warn('Failed to fetch banners (/brand/banner):', error)
        })

        // 如果有token 強制更新個人資料
        if (store.getters.token) {
          info().then(response => {
            const reqData = response.data
            store.dispatch('SetUserData', reqData.data)

            const walletInfo = reqData.data.walletInfo
            store.commit('SET_WALLET_INFO', walletInfo)

            const selectedWallet = walletInfo.find(wallet => wallet.wallet_type === store.state.app.selectedWallet.wallet_type)
            store.commit('SET_SELECTED_WALLET', selectedWallet)
          })
        }
        store.dispatch('UpdateCsUrl')
      }).catch((e) => {
        // 先解除 loading：網路層失敗、請求被拒發時 e.response 為 undefined，後續讀取不可再拋例外造成整站白頁
        // loadCompleted 只代表公開正文可讀，依賴品牌資料的頁面由 App.vue 依 brandStatus 擋住
        store.commit('SET_BRAND_STATUS', 'failed')
        store.commit('SET_LOADING', false)
        store.commit('SET_LOAD_COMPLETED', true)
        console.log(e)

        const toCriticalPage = message => {
          if (message) store.commit('SET_ERROR_MESSAGE', message)
          router.push({ path: '/critical' }, () => {})
        }

        try {
          const response = e?.response
          const data = response?.data?.data
          // 維護／IP 封鎖／403：原本呼叫的 toMaintainPage、toForbiddenPage 導向的 /maintain、/forbidden 沒有對應路由，
          // 會被隨後的 /critical 導航取代，實際落點一直是 /critical（文案由錯誤訊息帶入），維持此結果
          if (data?.type === 'maintain' || data?.type === 'ip_forbidden') {
            toCriticalPage(data.content)
          } else if (response?.status === 403) {
            toCriticalPage(response.data?.msg)
          } else {
            // 其他失敗（網路層、5xx…）：公開頁以兜底語系照常渲染，其餘頁面維持導向錯誤頁
            // 以網址解析判斷：需登入頁對未登入訪客會讓初次導航停在 pending，不能等 router.onReady
            const { route } = router.resolve(window.location.pathname + window.location.search)
            if (isPublicRoute(route)) {
              loadLanguagePack(language || DEFAULT_LANGUAGE, DEFAULT_LANGUAGE)
            } else {
              toCriticalPage(data?.content)
            }
          }
        } catch (err) {
          console.log(err)
        }
      })
    },
    render: h => h(App)
  }).$mount('#app')
}

initApp()
